/**
 * Member avatars stored in our own storage (Google Cloud Storage), like
 * creator and organization images already are, instead of as Sharetribe
 * profile images. On Sharetribe every avatar shown is a billed Image CDN
 * request, and every avatar in use costs a unique-image charge each month.
 *
 * The avatar is publicData.avatarImage = { id, link }. A user who still has
 * only a Sharetribe profile image keeps showing it until they upload a new
 * one (or the migration moves it).
 */
import { types as sdkTypes } from './sdkLoader';

const { UUID } = sdkTypes;

export const avatarImageOf = user => {
  const avatar = user?.attributes?.profile?.publicData?.avatarImage;
  return avatar && avatar.link ? avatar : null;
};

// A URL for places that need one string (Sendbird profile pictures).
export const avatarImageUrl = user => {
  const own = avatarImageOf(user);
  if (own) return own.link;
  const variants = user?.profileImage?.attributes?.variants || {};
  return variants['square-small2x']?.url || variants['square-small']?.url || null;
};

// An uploaded avatar in the shape of a Sharetribe image entity, so the forms
// that preview `uploadedImage` keep working unchanged.
export const avatarImageEntity = media => {
  const variant = (name, size) => ({ name, url: media.link, width: size, height: size });
  return {
    id: new UUID(media.id),
    type: 'image',
    link: media.link,
    attributes: {
      variants: {
        'square-small': variant('square-small', 240),
        'square-small2x': variant('square-small2x', 480),
      },
    },
  };
};

// The user as the form should preview them while a new avatar is uploaded.
export const withAvatarPreview = (user, uploadedImage) => {
  if (!uploadedImage?.link) return user;
  const profile = user?.attributes?.profile || {};
  return {
    ...user,
    attributes: {
      ...user.attributes,
      profile: {
        ...profile,
        publicData: { ...(profile.publicData || {}), avatarImage: { id: uploadedImage.id?.uuid, link: uploadedImage.link } },
      },
    },
  };
};
